// Sprint 9 global shortcuts, mounted once inside AppShell's TimerActionsProvider. Reuses the timer
// actions and the Add entry dialog; it owns no timer or entry logic of its own.
//
// Sprint 15 (architecture sprint-15 §9.3, owner decision 1): the keys follow the dashboard project selection on
// every page. On a shift project (selected, or an open shift for L) they start nothing and toast "This is a shift
// project" with a Start Shift action that opens the Dashboard (Start needs the location step). On a standard
// project T starts on it and Shift+T / L pre-fill it. With no selection they behave as before. It also remembers
// the project of any timer or shift started anywhere (useRememberActivityProject).
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { ON_SHIFT_MESSAGE, TIMER_ALREADY_RUNNING_MESSAGE } from '@/components/timer/timer-errors';
import { useGlobalShortcuts } from '@/hooks/use-global-shortcuts';
import { useRememberActivityProject, useSelectedProject } from '@/hooks/use-selected-project';
import { useTimer } from '@/hooks/use-timer';
import { shortcutsAllowedOn } from '@/lib/keyboard-shortcuts';

export const SHIFT_PROJECT_MESSAGE = 'This is a shift project';
export const START_SHIFT_ACTION = 'Start Shift';

export function GlobalShortcuts(): JSX.Element | null {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const enabled = shortcutsAllowedOn(pathname);
  const actions = useTimerActions();
  const timer = useTimer();
  const { selection } = useSelectedProject();
  useRememberActivityProject();
  const [logOpen, setLogOpen] = useState(false);

  /** Same gate as the start buttons: hidden while loading, disabled while pending, absent if a timer exists. */
  function guardedStart(start: () => void): () => void {
    return () => {
      if (timer.isLoading || actions.pending) return;
      // Sprint 13 (§8): T and Shift+T are refused while on shift.
      if (actions.onShift) {
        toast.error(ON_SHIFT_MESSAGE);
        return;
      }
      if (timer.data) {
        toast.error(TIMER_ALREADY_RUNNING_MESSAGE);
        return;
      }
      start();
    };
  }

  function shiftProjectToast(): void {
    toast(SHIFT_PROJECT_MESSAGE, { action: { label: START_SHIFT_ACTION, onClick: () => navigate('/') } });
  }

  // An open shift locks the selection to its project (kind 'shift'): T / Shift+T keep the ON_SHIFT refusal.
  const shiftSelected = selection?.kind === 'shift' && !actions.onShift;
  const projectId = selection?.kind === 'standard' ? (selection.project?.id ?? null) : null;

  useGlobalShortcuts(
    {
      'start-timer': shiftSelected ? shiftProjectToast : guardedStart(() => (projectId ? actions.startWith({ projectId, description: null }) : actions.startInstant())),
      'start-with-details': shiftSelected ? shiftProjectToast : guardedStart(() => (projectId ? actions.openStartWithDetails({ projectId }) : actions.openStartWithDetails())),
      // Owner decision 1 (A): L on a shift (or with a shift project selected) toasts too; past days stay on Time log.
      'log-time': selection?.kind === 'shift' ? shiftProjectToast : () => setLogOpen(true),
    },
    enabled,
  );

  if (!enabled) return null;
  return <EntryFormDialog open={logOpen} onOpenChange={setLogOpen} mode="create" {...(projectId ? { contextProjectId: projectId } : {})} />;
}
